import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Paper,
  Stack,
  Link,
  Avatar,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { toast } from "react-toastify";

// Renders a small publish-status chip from a tri-state value:
//   true  → Published, false → Pending, null/undefined → In feed (unknown)
const statusChip = (published) => {
  if (published === true)
    return (
      <Chip
        size="small"
        color="success"
        variant="outlined"
        icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
        label="Published"
        sx={{ height: 20, flexShrink: 0 }}
      />
    );
  if (published === false)
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        icon={<HourglassEmptyIcon sx={{ fontSize: "13px !important" }} />}
        label="Pending"
        sx={{ height: 20, flexShrink: 0 }}
      />
    );
  return (
    <Chip size="small" variant="outlined" label="In feed" sx={{ height: 20, flexShrink: 0 }} />
  );
};

// Per-platform accent colours (mirror PlatformSourceCard)
const PLATFORM_ACCENT = {
  YOUTUBE: "#FF0000",
  FACEBOOK: "#1877F2",
  INSTAGRAM: "#E1306C",
  TIKTOK: "#010101",
  DAILYMOTION: "#0066DC",
  MSN: "#00A4EF",
  PINTEREST: "#E60023",
  SNAPCHAT: "#B8B600",
  THREADS: "#000000",
  OTHERS: "#606060",
};
const accent = (p) => PLATFORM_ACCENT[p] || PLATFORM_ACCENT.OTHERS;

const FEED_TYPE_COLOR = { SOCIAL: "primary", PARTNER: "warning" };

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

/**
 * Shows a pulsing warning badge when a video is already published to one or more
 * feeds. Hover → rich summary tooltip; click → full per-feed / per-platform
 * details dialog with copyable / openable platform URLs.
 *
 * `status` shape: { published: bool, feeds: [{ feed_name, feed_type, brand,
 *   is_active, pub_date, platforms: [{ platform, url, thumbnail }] }] }
 */
export default function DuplicateWarning({ status }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);

  if (!status?.published || !status.feeds?.length) return null;

  const feeds = status.feeds;
  const feedCount = feeds.length;
  const platformCount = new Set(
    feeds.flatMap((f) => (f.platforms || []).map((p) => p.platform)),
  ).size;

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(
      () => toast.success("URL copied"),
      () => toast.error("Copy failed"),
    );
  };

  const tooltipContent = (
    <Box sx={{ p: 0.5, maxWidth: 300 }}>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
      >
        <WarningAmberIcon sx={{ fontSize: 16 }} /> Already published
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mb: 0.5, opacity: 0.85 }}>
        Found in {feedCount} feed{feedCount !== 1 ? "s" : ""} · {platformCount}{" "}
        platform{platformCount !== 1 ? "s" : ""}
      </Typography>
      {feeds.slice(0, 3).map((f) => (
        <Box key={f.feed_id} sx={{ mt: 0.75 }}>
          <Typography variant="caption" fontWeight={600} sx={{ display: "block" }}>
            {f.feed_name}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, mt: 0.3 }}>
            {(f.platforms || []).map((p) => (
              <Box
                key={p.platform}
                component="span"
                sx={{
                  fontSize: 10,
                  px: 0.6,
                  py: 0.15,
                  borderRadius: 0.5,
                  bgcolor: accent(p.platform),
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                {p.platform}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
      {feedCount > 3 && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.75, fontStyle: "italic", opacity: 0.85 }}
        >
          +{feedCount - 3} more feed{feedCount - 3 !== 1 ? "s" : ""}…
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: "block", mt: 0.75, opacity: 0.7 }}>
        Click to see full details &amp; URLs
      </Typography>
    </Box>
  );

  return (
    <>
      <Tooltip title={tooltipContent} arrow placement="top">
        <Badge
          badgeContent={feedCount}
          color="error"
          overlap="circular"
          sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 16, minWidth: 16 } }}
        >
          <IconButton
            size="small"
            onClick={() => setOpen(true)}
            aria-label={`Already published in ${feedCount} feed${feedCount !== 1 ? "s" : ""} — view details`}
            sx={{
              minWidth: { xs: 44, sm: 36 },
              minHeight: { xs: 44, sm: 36 },
              color: "warning.dark",
              bgcolor: alpha(theme.palette.warning.main, 0.14),
              animation: "dupPulse 2s infinite",
              "@keyframes dupPulse": {
                "0%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.45)}` },
                "70%": { boxShadow: `0 0 0 8px ${alpha(theme.palette.warning.main, 0)}` },
                "100%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0)}` },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.26) },
              "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            }}
          >
            <WarningAmberIcon fontSize="small" />
          </IconButton>
        </Badge>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        aria-labelledby="dup-warning-title"
        PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2, overflow: "hidden" } }}
      >
        {/* Gradient header */}
        <Box
          sx={{
            position: "relative",
            p: { xs: 2, sm: 2.5 },
            pr: 7,
            color: "warning.contrastText",
            bgcolor: "warning.dark",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.common.white, 0.22), color: "inherit" }}>
              <WarningAmberIcon />
            </Avatar>
            <Box>
              <Typography id="dup-warning-title" variant="h6" component="h2" fontWeight={600} lineHeight={1.2}>
                Duplication Detected
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.92 }}>
                Already published in {feedCount} feed{feedCount !== 1 ? "s" : ""} ·{" "}
                {platformCount} platform{platformCount !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setOpen(false)}
            aria-label="Close duplicate details"
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              minWidth: { xs: 44, sm: 36 },
              minHeight: { xs: 44, sm: 36 },
              color: "inherit",
              "&:focus-visible": { outline: "2px solid currentColor", outlineOffset: 2 },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "background.default" }}>
          <Stack spacing={1.5}>
            {feeds.map((f) => (
              <Paper
                key={f.feed_id}
                variant="outlined"
                sx={{ borderRadius: 2, overflow: "hidden" }}
              >
                {/* Feed header */}
                <Box
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    bgcolor: "background.paper",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <RssFeedIcon fontSize="small" color="action" />
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {f.feed_name}
                    </Typography>
                    {f.brand && (
                      <Typography variant="caption" color="text.secondary">
                        {f.brand}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {f.all_published && (
                      <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                        label="All published"
                      />
                    )}
                    <Chip
                      size="small"
                      label={f.feed_type}
                      color={FEED_TYPE_COLOR[f.feed_type] || "default"}
                      variant="outlined"
                    />
                    {!f.is_active && (
                      <Chip size="small" label="inactive" variant="outlined" />
                    )}
                  </Stack>
                </Box>

                {/* Platform URLs */}
                <Box sx={{ p: 1.5 }}>
                  {f.pub_date && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Published {fmtDate(f.pub_date)}
                    </Typography>
                  )}
                  <Stack spacing={1}>
                    {(f.platforms || []).length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No platform URLs recorded for this feed.
                      </Typography>
                    )}
                    {(f.platforms || []).map((p) => (
                      <Box
                        key={p.platform}
                        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
                      >
                        <Chip
                          size="small"
                          label={p.platform}
                          sx={{
                            bgcolor: accent(p.platform),
                            color: "#fff",
                            fontWeight: 600,
                            height: 20,
                            flexShrink: 0,
                          }}
                        />
                        {statusChip(p.published)}
                        {p.url ? (
                          <>
                            <Link
                              href={p.url}
                              target="_blank"
                              rel="noopener"
                              variant="caption"
                              underline="hover"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.3,
                                fontWeight: 600,
                                cursor: "pointer",
                                minHeight: { xs: 44, sm: 36 },
                                "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                              }}
                            >
                              View video
                              <OpenInNewIcon sx={{ fontSize: 13 }} />
                            </Link>
                            <Tooltip title="Copy URL">
                              <IconButton
                                onClick={() => copy(p.url)}
                                aria-label={`Copy ${p.platform} URL`}
                                sx={{ minWidth: { xs: 44, sm: 36 }, minHeight: { xs: 44, sm: 36 }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" } }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ flex: 1 }}
                          >
                            No URL recorded
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Paper>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, flexWrap: "wrap", gap: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: "1 1 200px", ml: 1 }}
          >
            This is only a warning — you can still publish this video.
          </Typography>
          <Button onClick={() => setOpen(false)} variant="contained" sx={{ minHeight: { xs: 44, sm: 36 }, minWidth: 96 }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
